import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {WizardApiError} from '../../api'
import {getPmAccessStatus, isPmAccessFailure, PmOtpChallenge, sendPmOtp, verifyPmOtp} from '../../api/pmAccess'
import {OTPInput} from '../../../common/component/OTPInput'
import {useRetryCountdown} from '../common/useRetryCountdown'
import {PmAccessContext} from './PmAccessContext'
import styles from './PmAccessGate.module.css'

type Props = {children: React.ReactNode}

const messageFor = (error: unknown) => error instanceof Error ? error.message : 'Unable to verify access. Please try again.'

function PmAccessSession({token, children}: Props & {token: string}) {
  const [trusted, setTrusted] = useState(false)
  const [checking, setChecking] = useState(true)
  const [challenge, setChallenge] = useState<PmOtpChallenge | null>(null)
  const [maskedMobile, setMaskedMobile] = useState('')
  const [code, setCode] = useState('')
  const [inputKey, setInputKey] = useState(0)
  const [message, setMessage] = useState<string | null>(null)
  const [failure, setFailure] = useState<Error | null>(null)
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const [requiresNewCode, setRequiresNewCode] = useState(false)
  const busyRef = useRef(false)
  const {seconds, start} = useRetryCountdown()

  const handleFailure = useCallback((error: unknown) => {
    if (!isPmAccessFailure(error)) return false
    setTrusted(false)
    setChallenge(null)
    setCode('')
    if (error.code === 'PM_OTP_REQUIRED') {
      setChecking(true)
      setFailure(null)
      setReload(value => value + 1)
    } else {
      setFailure(error)
      setChecking(false)
    }
    return true
  }, [])

  const sendCode = useCallback(async () => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setMessage(null)
    try {
      const result = await sendPmOtp(token)
      setChallenge(result)
      setMaskedMobile(result.maskedMobile)
      setRequiresNewCode(false)
      setCode('')
      setInputKey(value => value + 1)
      start(result.retryAfterSeconds)
    } catch (error) {
      if (!handleFailure(error)) {
        setMessage(messageFor(error))
        if (error instanceof WizardApiError) start(error.retryAfterSeconds)
      }
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [handleFailure, start, token])

  useEffect(() => {
    let cancelled = false
    setChecking(true)
    setFailure(null)
    setMessage(null)
    const check = async () => {
      try {
        const status = await getPmAccessStatus(token)
        if (cancelled) return
        setTrusted(status.trusted)
        setMaskedMobile(status.maskedMobile)
        start(status.retryAfterSeconds)
        if (!status.trusted) {
          if (status.challengeId && status.otpExpiresAtUtc) {
            setChallenge({challengeId: status.challengeId, expiresAtUtc: status.otpExpiresAtUtc,
              maskedMobile: status.maskedMobile, retryAfterSeconds: status.retryAfterSeconds})
            setRequiresNewCode(false)
          } else if (!status.retryAfterSeconds) {
            await sendCode()
          }
        }
      } catch (error) {
        if (!cancelled) setFailure(error instanceof Error ? error : new Error(messageFor(error)))
      } finally {
        if (!cancelled) setChecking(false)
      }
    }
    void check()
    return () => {cancelled = true}
  }, [reload, sendCode, start, token])

  useEffect(() => {
    if (!trusted) return
    let cancelled = false
    const checkOnReturn = async () => {
      if (document.visibilityState === 'hidden') return
      try {
        const status = await getPmAccessStatus(token)
        if (!cancelled && !status.trusted) {
          handleFailure(new WizardApiError('Please verify access again.', {code: 'PM_OTP_REQUIRED'}))
        }
      } catch (error) {
        if (!cancelled) {
          if (!handleFailure(error)) {
            setTrusted(false)
            setFailure(error instanceof Error ? error : new Error(messageFor(error)))
          }
        }
      }
    }
    window.addEventListener('focus', checkOnReturn)
    document.addEventListener('visibilitychange', checkOnReturn)
    return () => {
      cancelled = true
      window.removeEventListener('focus', checkOnReturn)
      document.removeEventListener('visibilitychange', checkOnReturn)
    }
  }, [handleFailure, token, trusted])

  const verify = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!challenge || code.length !== 6 || busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setMessage(null)
    try {
      const result = await verifyPmOtp(token, challenge.challengeId, code)
      if (!result.verified) {
        setMessage(result.message || 'Unable to verify this code.')
        setRequiresNewCode(result.requiresNewCode)
        return
      }
      const status = await getPmAccessStatus(token)
      if (status.trusted) {
        setTrusted(true)
        setChallenge(null)
        setCode('')
      } else {
        setRequiresNewCode(true)
        setMessage('Your browser could not keep the verification cookie. Please allow cookies and request a new code.')
      }
    } catch (error) {
      if (!handleFailure(error)) {
        setMessage(messageFor(error))
        if (error instanceof WizardApiError) start(error.retryAfterSeconds)
      }
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  if (trusted) return <PmAccessContext.Provider value={handleFailure}>{children}</PmAccessContext.Provider>

  const redirectUrl = failure instanceof WizardApiError ? failure.redirectUrl : undefined
  return (
    <main className='wizard-page px-3 px-md-6 py-6 py-md-10'>
      <div className='mx-auto w-100' style={{maxWidth: 560}}>
        <form onSubmit={verify}>
          <section className='wizard-card wizard-auth-card bg-white rounded-4 shadow-sm p-4 p-md-6' aria-labelledby='pm-access-title'>
            <div className='text-center text-dark wizard-auth-head mb-10'>
              <h1 id='pm-access-title' className='wizard-auth-title wizard-auth-title-otp mt-10'>Verify submission access</h1>
              {!checking && !failure && <>
                <p className='wizard-auth-subtitle'>
                  {challenge ? 'We sent a 6-digit code' : 'We will send a 6-digit code'} to the nominated mobile
                  {maskedMobile && <><br /><strong className='wizard-auth-subtitle-strong'>{maskedMobile}</strong></>}
                </p>
                <p className='text-muted mt-4 mb-0'>Use the code to open this submission. The delivery number is set by the sender.</p>
              </>}
            </div>
            {checking ? <p role='status' className='text-center'>Checking secure access...</p> : failure ? <div className='text-center'>
              <p role='alert' className='text-danger'>{failure.message}</p>
              {redirectUrl && <a className='btn wizard-primary-action w-100 mb-3' href={redirectUrl}>Provide email</a>}
              <button className='btn btn-light' type='button' onClick={() => setReload(value => value + 1)}>Retry</button>
            </div> : <>
              {message && <p role='alert' className='text-danger text-center'>{message}</p>}
              {challenge && <div className={`text-center text-dark wizard-otp-row ${styles.otpRow}`} role='group' aria-labelledby='pm-code-label'>
                <p id='pm-code-label' className='visually-hidden'>Enter the six-digit SMS code</p>
                <OTPInput<{code: string}>
                  key={inputKey}
                  name='code'
                  disabled={busy || requiresNewCode}
                  onChange={setCode}
                  className='wizard-otp-input my-3 justify-content-center'
                />
              </div>}
              <div className='text-center mt-10 mb-6'>
                <button className='btn btn-link wizard-otp-resend' type='button' disabled={busy || seconds > 0} onClick={() => void sendCode()}>
                  {challenge ? 'Resend Code' : 'Send code'}
                </button>
                {seconds > 0 && <span className='small text-muted ms-2'>({seconds}s)</span>}
              </div>
            </>}
          </section>
          {!checking && !failure && challenge && <div className='wizard-footer border-top mt-10'>
            <button className='btn wizard-primary-action w-100' type='submit' disabled={busy || requiresNewCode || code.length !== 6}>
              {busy ? 'Please wait...' : 'Verify and open submission'}
            </button>
          </div>}
        </form>
      </div>
    </main>
  )
}

export default function PmAccessGate({children}: Props) {
  const {token = ''} = useParams<{token: string}>()
  return <PmAccessSession key={token} token={token}>{children}</PmAccessSession>
}
